import React from 'react';
import StoreIcon from '@mui/icons-material/Store';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import CommentIcon from '@mui/icons-material/Comment';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { useSession } from '../contexts/SessionContext';
import { useCalendar } from '../contexts/CalendarContext';
import { getPossessionFields, getExtraFields, fieldStateKey } from '../utils/dataFields';
import { formatServicePrice, STARTING_AT_LABEL } from '../utils/serviceDisplay';
import { stepShown, VISIT_TYPE_LABELS } from '../utils/visitType';

const BookingRecap = ({ isProgressCard = false, onEditSection, completedSteps = new Set(), onEditVisitType }) => {
  const { selectedLocation, selectedServices, selectedTimeSlot, personalInfo, visitType, visitTypeOffered } = useSession();
  const { fields, enableWaiterDropoff } = useCalendar();
  // CIN-7181: 'Appointment Type' appears only when the choice step was shown
  // (feature on + both types offered) and a choice exists.
  const visitTypeLabel = stepShown(enableWaiterDropoff, visitTypeOffered || []) ? VISIT_TYPE_LABELS[visitType] : undefined;
  // Generic possession summary (vehicle / animal / real_estate) from the configured fields.
  const possessionSummary = getPossessionFields(fields)
    .map((f) => personalInfo[fieldStateKey(f)])
    .filter(Boolean)
    .join(' ');
  const possessionLabel = fields?.possession_label || 'Details';
  // Additional Information (fields.extra) values that were filled in.
  const extraLabel = fields?.extra_label || 'Additional Comments';
  const extraRecapFields = getExtraFields(fields).filter((f) => {
    const v = personalInfo[fieldStateKey(f)];
    return typeof v === 'boolean' ? v : v !== undefined && v !== null && String(v).trim() !== '';
  });
  // Helper function to format phone number as (xxx)xxx-xxxx
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove all non-numeric characters
    const cleaned = phoneNumber.replace(/[^\d]/g, '');
    
    // Format based on length
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    
    // Return original if not 10 digits
    return phoneNumber;
  };

  // Helper function to format time estimate
  const formatDuration = (minutes) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}min`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}min`;
    }
  };

  // Helper function to format address (same as LocationSelection)
  const formatAddress = (location) => {
    if (!location) return [];
    const addressLines = [];
    
    // First line: address1
    if (location.address1) {
      addressLines.push(location.address1);
    }
    
    // Second line: address2 (if exists)
    if (location.address2) {
      addressLines.push(location.address2);
    }
    
    // Third line: city, state zip
    const cityStateZip = [];
    if (location.city) cityStateZip.push(location.city);
    if (location.state) cityStateZip.push(location.state);
    if (location.zip_code) cityStateZip.push(location.zip_code);
    
    if (cityStateZip.length > 0) {
      // Format as "City, State Zip"
      let formattedLine = '';
      if (location.city && location.state) {
        formattedLine = `${location.city}, ${location.state}`;
        if (location.zip_code) {
          formattedLine += ` ${location.zip_code}`;
        }
      } else {
        formattedLine = cityStateZip.join(' ');
      }
      addressLines.push(formattedLine);
    }
    
    return addressLines;
  };

  return (
    <div className={`step-content recap ${isProgressCard ? 'progress-card-recap' : ''}`}>
      {selectedLocation && (!isProgressCard || completedSteps.has(1)) && (
        <div className={`recap-section ${isProgressCard ? 'editable' : ''}`}>
          <div className="recap-header">
            <StoreIcon className="recap-header-icon" />
            <h3>Location</h3>
            {isProgressCard && onEditSection && (
              <EditIcon 
                className="edit-icon" 
                onClick={() => onEditSection(1)}
                style={{ cursor: 'pointer', fontSize: '16px', marginLeft: 'auto' }}
              />
            )}
          </div>
          <div className="recap-item">
            <strong>{selectedLocation?.name}</strong>
            {formatAddress(selectedLocation).map((line, index) => (
              <p key={index} className="body-text">{line}</p>
            ))}
          </div>
        </div>
      )}

      {!selectedLocation && isProgressCard && completedSteps.has(1) && (
        <div className="recap-section empty">
          <div className="recap-header">
            <StoreIcon className="recap-header-icon" />
            <h3>Location</h3>
          </div>
          <div className="recap-item">
            <p className="body-text" style={{ color: '#999', fontStyle: 'italic' }}>Not selected</p>
          </div>
        </div>
      )}

      {(selectedServices.length > 0 || isProgressCard) && (!isProgressCard || completedSteps.has(2)) && (
        <div className={`recap-section ${isProgressCard ? 'editable' : ''}`}>
          <div className="recap-header">
            <MiscellaneousServicesIcon className="recap-header-icon" />
            <h3>Services</h3>
            {isProgressCard && onEditSection && (
              <EditIcon 
                className="edit-icon" 
                onClick={() => onEditSection(2)}
                style={{ cursor: 'pointer', fontSize: '16px', marginLeft: 'auto' }}
              />
            )}
          </div>
          {selectedServices.length > 0 ? (
            selectedServices.map(service => {
              const priceText = formatServicePrice(service);
              return (
                <div key={service.id} className="recap-item">
                  <strong>{service.name}</strong>
                  <p className="caption" data-testid="recap-service-caption">
                    {service.time_estimate && formatDuration(service.time_estimate)}
                    {service.time_estimate && priceText && ' - '}
                    {priceText && `${STARTING_AT_LABEL} ${priceText}`}
                  </p>
                </div>
              );
            })
          ) : isProgressCard && completedSteps.has(2) && (
            <div className="recap-item">
              <p className="body-text" style={{ color: '#999', fontStyle: 'italic' }}>Not selected</p>
            </div>
          )}
        </div>
      )}

      {visitTypeLabel && (
        <div className="recap-section" data-testid="recap-appointment-type-section">
          <div className="recap-header">
            <EventNoteIcon className="recap-header-icon" />
            <h3>Appointment Type</h3>
            {onEditVisitType && (
              <button
                type="button"
                className="recap-edit-button"
                data-testid="recap-appointment-type-edit"
                aria-label="Change appointment type"
                onClick={onEditVisitType}
              >
                <EditIcon className="edit-icon" style={{ fontSize: '16px' }} />
              </button>
            )}
          </div>
          <div className="recap-item">
            <p className="body-text" data-testid="recap-appointment-type">{visitTypeLabel}</p>
          </div>
        </div>
      )}

      {(selectedTimeSlot || isProgressCard) && (!isProgressCard || completedSteps.has(3)) && (
        <div className={`recap-section ${isProgressCard ? 'editable' : ''}`}>
          <div className="recap-header">
            <CalendarTodayIcon className="recap-header-icon" />
            <h3>Date & Time</h3>
            {isProgressCard && onEditSection && (
              <EditIcon
                className="edit-icon"
                onClick={() => onEditSection(3)}
                style={{ cursor: 'pointer', fontSize: '16px', marginLeft: 'auto' }}
              />
            )}
          </div>
          <div className="recap-item">
            {selectedTimeSlot ? (
              <>
                <p className="body-text">{new Date(selectedTimeSlot).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</p>
                <p className="body-text" style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                  {new Date(selectedTimeSlot).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}
                </p>
              </>
            ) : (
              <p className="body-text" style={{ color: '#999', fontStyle: 'italic' }}>Not selected</p>
            )}
          </div>
        </div>
      )}

      {(personalInfo.first_name || personalInfo.last_name || personalInfo.phone || possessionSummary || isProgressCard) && (!isProgressCard || completedSteps.has(4)) && (
        <div className={`recap-section ${isProgressCard ? 'editable' : ''}`}>
          <div className="recap-header">
            <PersonIcon className="recap-header-icon" />
            <h3>Personal Information</h3>
            {isProgressCard && onEditSection && (
              <EditIcon
                className="edit-icon"
                onClick={() => onEditSection(4)}
                style={{ cursor: 'pointer', fontSize: '16px', marginLeft: 'auto' }}
              />
            )}
          </div>
          <div className="recap-item">
            {personalInfo.first_name || personalInfo.last_name ? (
              <p className="body-text"><strong>Name:</strong> {personalInfo.first_name} {personalInfo.last_name}</p>
            ) : isProgressCard && completedSteps.has(4) && (
              <p className="body-text" style={{ color: '#999', fontStyle: 'italic' }}>Name not provided</p>
            )}
            {personalInfo.phone ? (
              <p className="body-text"><strong>Phone:</strong> {formatPhoneNumber(personalInfo.phone)}</p>
            ) : isProgressCard && (personalInfo.first_name || personalInfo.last_name) && completedSteps.has(4) && (
              <p className="body-text" style={{ color: '#999', fontStyle: 'italic' }}>Phone not provided</p>
            )}
            {possessionSummary ? (
              <p className="body-text"><strong>{possessionLabel}:</strong> {possessionSummary}</p>
            ) : isProgressCard && completedSteps.has(4) && (
              <p className="body-text" style={{ color: '#999', fontStyle: 'italic' }}>Not provided</p>
            )}
          </div>
        </div>
      )}

      {extraRecapFields.length > 0 && (
        <div className={`recap-section ${isProgressCard ? 'editable' : ''}`}>
          <div className="recap-header">
            <CommentIcon className="recap-header-icon" />
            <h3>{extraLabel}</h3>
            {isProgressCard && onEditSection && (
              <EditIcon
                className="edit-icon"
                onClick={() => onEditSection(4)}
                style={{ cursor: 'pointer', fontSize: '16px', marginLeft: 'auto' }}
              />
            )}
          </div>
          <div className="recap-item">
            {extraRecapFields.map((f) => {
              const value = personalInfo[fieldStateKey(f)];
              const display = typeof value === 'boolean' ? 'Yes' : value;
              return (
                <p key={f.id} className="body-text" style={{ whiteSpace: 'pre-wrap' }}>
                  <strong>{f.name}:</strong> {display}
                </p>
              );
            })}
          </div>
        </div>
      )}

      {/* CIN-6441 (P4): cancellation/refund policy visibility. No such copy
          existed on the review step or success screen before. Cinch bookings
          are managed by the shop, so point the customer there. Full review step
          only (not the progress-card sidebar variant). */}
      {!isProgressCard && (
        <p
          className="body-text"
          data-testid="cancellation-policy-note"
          style={{ marginTop: '16px', color: '#666', fontSize: '13px', textAlign: 'center' }}
        >
          Need to change or cancel? Contact the shop directly.
        </p>
      )}
    </div>
  );
};

export default BookingRecap;